import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { WebLedgerDecision } from './goal-ledger';
import { decisionControl, decisionOptions, type DecisionAnswer } from './decisions';

/**
 * ANSWERING a blocking question FROM THE TASK ROW — which shape the row gets, and when it must refuse to
 * try.
 *
 * The row now carries the question (`readTaskDecisionPrompts`, common-lib) and this module decides what
 * the reader can do about it *there*. It contains no transport and no React: the answer still goes
 * through `lib/decisions.ts` → `POST /api/decision-verdict`, the same single write path the goal
 * ledger's `DecisionAnswerBlock` uses, and the "Answer this" escape hatch opens that very component. One
 * write path, one full control; this file only chooses between "chips on the row" and "open the control".
 *
 * ## The overflow rule, and why it is a rule
 *
 * A task row is one line of a scannable list. Chips are for what FITS on it — and everything that does
 * not fit collapses to a single `Answer this` chip rather than growing the row:
 *
 *  - **more than {@link TASK_DECISION_MAX_CHIPS} options** — four alternatives wrap onto a second line
 *    and the list stops being scannable;
 *  - **a question longer than {@link TASK_DECISION_QUESTION_LIMIT}** — an approval whose question needs
 *    a paragraph is not an approval the reader should give from a list;
 *  - **an `ANSWER`** — free text has no chip shape at all;
 *  - **labels that collide once clamped** — see {@link chipLabel}. Two chips reading the same words are
 *    worse than no chips, because the reader taps one believing they know which.
 *
 * There is deliberately **no "ask me later" chip**. A decision's vocabulary is approve / refuse / choose
 * / answer; a fourth pseudo-answer that quietly hides the row is how a blocked plan goes silent while
 * everyone believes it was dealt with.
 */

/** How many answer chips may sit on one row. Beyond it the row collapses to `Answer this`. */
export const TASK_DECISION_MAX_CHIPS = 3;

/** How long a chip's label may read before it is clamped with an ellipsis. */
export const TASK_DECISION_LABEL_LIMIT = 28;

/**
 * How long a question may be and still be answerable from the row.
 *
 * Not a layout measurement — it is a judgement about what may be approved at a glance. A question that
 * needs more than a sentence needs its detail, which is what the full control shows.
 */
export const TASK_DECISION_QUESTION_LIMIT = 140;

/** The prompts this row is waiting on, in the order the task names them. Empty when there are none. */
export function taskDecisionPrompts(task: { decisionPrompts?: TaskDecisionPrompt[] }): TaskDecisionPrompt[] {
  return task.decisionPrompts ?? [];
}

/**
 * The task NAMES a blocker whose question we could not read — the tri-state's third state, which must
 * never render as the first. `true` only when there are no prompts either: a task whose prompts DID
 * arrive is answerable, and the flag is absent on it by construction.
 */
export function taskDecisionsUnavailable(task: {
  decisionPrompts?: TaskDecisionPrompt[];
  decisionPromptsUnavailable?: boolean;
}): boolean {
  return task.decisionPromptsUnavailable === true && taskDecisionPrompts(task).length === 0;
}

/**
 * A prompt as the decision the existing answering code already speaks.
 *
 * This is what makes "reuse the full control" literal rather than aspirational: `decisionControl`,
 * `decisionOptions`, `useDecisionAnswer`, `holdPlanVersion` and `DecisionAnswerBlock` all take a
 * `WebLedgerDecision`, and a prompt IS one, minus the history a ledger row carries.
 *
 * Two fields are load-bearing:
 *
 *  - **`status: 'OPEN'`** is a fact, not a default. `readTaskDecisionPrompts` emits a prompt only for an
 *    OPEN decision on an unfinished task — a closed one behind an unresolved edge is deliberately
 *    dropped there — so `isAnswerable` is true by construction and the control renders.
 *  - **`planVersion` crosses VERBATIM, absence included.** It is echoed on the verdict and compared for
 *    exact equality, `Optional`-empty against `Optional`-empty; substituting `'0'` refuses a perfectly
 *    good answer to a question raised before the goal was ever revised. The hold is taken from the row
 *    that was RENDERED (`holdPlanVersion`, keyed on the decision id), never re-read at submit.
 */
export function promptAsDecision(prompt: TaskDecisionPrompt): WebLedgerDecision {
  return {
    id: prompt.decisionId,
    status: 'OPEN',
    kind: prompt.kind,
    ...(prompt.question ? { question: prompt.question } : {}),
    ...(prompt.detail ? { detail: prompt.detail } : {}),
    ...(prompt.options ? { options: prompt.options } : {}),
    ...(prompt.planVersion ? { planVersion: prompt.planVersion } : {}),
    ...(prompt.dueBy ? { dueBy: prompt.dueBy } : {}),
  };
}

/** One tappable answer on the row. `labelKey` is localized at the render site; `label` is the agent's
 *  own words for an option and is shown as authored (clamped). Exactly one of the two is set. */
export interface TaskDecisionChip {
  /** Which control spins while this one is in flight — an option id, or `approve` / `refuse`. */
  id: string;
  labelKey?: string;
  label?: string;
  /** Whether the label was clamped, so the render site can carry the full text as a `title`. */
  fullLabel?: string;
  /** The answer this chip commits to. Nothing else about a chip decides what is sent. */
  answer: DecisionAnswer;
}

/**
 * What the row offers for one question.
 *
 * `chips` answers in place. `full` is the single `Answer this` chip that opens the ledger's own control
 * — never a second control, and never a row that grew into one. `unanswerable` is a question we can show
 * and cannot answer here at all (a `CHOOSE` that arrived with no options); it is NOT rendered as an
 * empty picker and NOT rendered as "nothing to do".
 */
export type TaskDecisionShape =
  | { mode: 'chips'; chips: TaskDecisionChip[] }
  | { mode: 'full' }
  | { mode: 'unanswerable' };

/**
 * A chip label at chip length. Clamped on a WORD boundary where there is one, so a truncation reads as a
 * shortened phrase rather than a severed word.
 */
export function chipLabel(label: string, limit: number = TASK_DECISION_LABEL_LIMIT): { text: string; clamped: boolean } {
  const trimmed = label.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= limit) return { text: trimmed, clamped: false };
  const cut = trimmed.slice(0, limit);
  const space = cut.lastIndexOf(' ');
  const head = space > limit / 2 ? cut.slice(0, space) : cut;
  return { text: `${head.trimEnd()}…`, clamped: true };
}

/** The question as it reads on the row, or undefined when the decision carried none (a blank question is
 *  a real row — the caller falls back to the ledger's "Waiting on you" title, never to bare buttons). */
export function promptQuestion(prompt: TaskDecisionPrompt): string | undefined {
  const question = prompt.question?.trim().replace(/\s+/g, ' ');
  return question || undefined;
}

/**
 * The shape this question gets ON THE ROW.
 *
 * `decisionControl` — the ledger's own kind→control rule, reused rather than re-derived, so an
 * unrecognised kind falls to the two-button APPROVE shape here for exactly the reason it does there:
 * `resolution` is required on every verdict and orthogonal to kind, so ACCEPTED/REFUSED is legal for a
 * kind this build has never heard of, and it is the only shape that needs no kind-specific data to draw.
 * A row that cannot name the kind is never a row that cannot be answered.
 */
export function taskDecisionShape(prompt: TaskDecisionPrompt): TaskDecisionShape {
  const decision = promptAsDecision(prompt);
  const control = decisionControl(decision);
  if (control === 'unanswerable') return { mode: 'unanswerable' };
  // Free text has no chip shape, and a question too long to sit on a row must not be approved off one.
  const question = promptQuestion(prompt);
  if (control === 'answer') return { mode: 'full' };
  if (question && question.length > TASK_DECISION_QUESTION_LIMIT) return { mode: 'full' };

  if (control === 'choose') {
    const options = decisionOptions(decision);
    if (options.length > TASK_DECISION_MAX_CHIPS) return { mode: 'full' };
    const chips = options.map((option) => {
      const clamp = chipLabel(option.label);
      return {
        id: option.id,
        label: clamp.text,
        ...(clamp.clamped ? { fullLabel: option.label.trim() } : {}),
        answer: { resolution: 'ACCEPTED' as const, choice: option.id },
      };
    });
    // Two chips reading the same words are unanswerable in practice: the reader picks one believing they
    // know which, and the difference lived in the characters we cut. The full control shows both whole.
    const labels = new Set(chips.map((chip) => chip.label));
    if (labels.size < chips.length) return { mode: 'full' };
    return { mode: 'chips', chips };
  }

  // APPROVE / CONFIRM — and every unrecognised kind. Two chips, the same words the ledger uses.
  const confirm = control === 'confirm';
  return {
    mode: 'chips',
    chips: [
      { id: 'approve', labelKey: confirm ? 'goals.decision.confirm' : 'goals.decision.approve', answer: { resolution: 'ACCEPTED' } },
      { id: 'refuse', labelKey: confirm ? 'goals.decision.deny' : 'goals.decision.decline', answer: { resolution: 'REFUSED' } },
    ],
  };
}
